// MysteryMission.js — Daily Mystery Mission card for NeevStar home screen
import React, { useState, useEffect } from 'react';
import { playSound } from '../../utils/appUtils';
import { supabase } from '../../supabase';

// IST date string for consistent daily keys
function todayIST() {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 10);
}

// ── Supabase helpers ─────────────────────────────────────────
// Cache email in memory — cleared on auth state change
let _cachedEmail = null;
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') _cachedEmail = null;
  else if (session?.user?.email) _cachedEmail = session.user.email;
});
async function getEmailFromSession() {
  if (_cachedEmail) return _cachedEmail;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    _cachedEmail = session?.user?.email || null;
    return _cachedEmail;
  } catch { return null; }
}

async function loadMissionFromDB(email) {
  if (!email) return null;
  const { data } = await supabase
    .from('ns_mission_progress')
    .select('*')
    .eq('parent_email', email)
    .eq('mission_date', todayIST())
    .single();
  return data || null;
}

async function saveMissionToDB(email, missionId, progress, claimed) {
  if (!email) return;
  await supabase.from('ns_mission_progress').upsert({
    parent_email: email,
    mission_date: todayIST(),
    mission_id: missionId,
    progress,
    claimed,
  }, { onConflict: 'parent_email,mission_date' });
}

const css = `
@keyframes mm-badge-pulse {
  0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,45,85,0.6); }
  50%      { transform: scale(1.08); box-shadow: 0 0 0 6px rgba(255,45,85,0); }
}
@keyframes mmLockBounce {
  0%,100% { transform: scale(1) rotate(0deg); }
  25%      { transform: scale(1.2) rotate(-8deg); }
  75%      { transform: scale(1.2) rotate(8deg); }
}
@keyframes mmReveal {
  0%   { transform: scale(0.7) rotateY(90deg); opacity: 0; }
  60%  { transform: scale(1.05) rotateY(-5deg); opacity: 1; }
  100% { transform: scale(1) rotateY(0deg); opacity: 1; }
}
@keyframes mmProgressFill {
  0%   { width: 0%; }
  100% { width: var(--pct); }
}
@keyframes mmGoldGlow {
  0%,100% { box-shadow: 0 0 12px rgba(255,214,0,0.3), 0 2px 12px rgba(0,0,0,0.08); }
  50%      { box-shadow: 0 0 28px rgba(255,214,0,0.6), 0 4px 20px rgba(0,0,0,0.1); }
}
@keyframes mmComplete {
  0%   { transform: scale(1); }
  30%  { transform: scale(1.06); }
  60%  { transform: scale(0.97); }
  100% { transform: scale(1); }
}
.mm-card {
  border-radius: 20px;
  border: 2px solid rgba(255,214,0,0.45);
  background: linear-gradient(135deg, #0D1B4B 0%, #1A0533 100%);
  animation: mmGoldGlow 2.5s ease-in-out infinite;
  overflow: hidden;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  transition: transform 0.15s;
}
.mm-card:active { transform: scale(0.98); }
.mm-lock-icon {
  font-size: 32px;
  animation: mmLockBounce 1.8s ease-in-out infinite;
  display: inline-block;
}
.mm-revealed { animation: mmReveal 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards; }
.mm-complete-pulse { animation: mmComplete 0.5s ease-in-out; }
`;

const MISSIONS = [
  { id: 'm1', icon: '🔬', text: 'Answer 5 questions in Science', reward: '+30 NeevCoins', target: 5, type: 'science_q', coins: 30,
    luckyAnnounce: (n,pet) => `${n}, I have a MISSION for you today! 🔬 Science time — 5 questions. I believe in you!`,
    luckyComplete: (n,pet) => `YOU DID IT!! I knew you could! Science champion right here! 🔬⭐` },
  { id: 'm2', icon: '🔥', text: 'Log in 3 days in a row', reward: 'Streak Shield', target: 3, type: 'streak', coins: 20,
    luckyAnnounce: (n,pet) => `${n}! Today's adventure: build your streak to 3 days! I'll be here every single day waiting 🔥`,
    luckyComplete: (n,pet) => `3 DAYS IN A ROW! I am literally glowing right now! 🔥` },
  { id: 'm3', icon: '⭐', text: 'Complete a quiz without a wrong answer', reward: 'Perfect Star + 25 coins', target: 1, type: 'perfect_quiz', coins: 25,
    luckyAnnounce: (n,pet) => `${n}, today's secret mission: a PERFECT quiz. Zero mistakes. Can you do it? I think you can! ⭐`,
    luckyComplete: (n,pet) => `PERFECT SCORE!! I told the stars about you! They said WOW! ⭐` },
  { id: 'm4', icon: '🧭', text: 'Try a subject you haven\'t done today', reward: 'Explorer Badge + 20 coins', target: 1, type: 'new_vertical', coins: 20,
    luckyAnnounce: (n,pet) => `${n}! Adventure time — try something NEW today! Explore a subject you haven't touched! 🧭`,
    luckyComplete: (n,pet) => `Explorer mode: ACTIVATED! Look at you trying new things! 🧭` },
  { id: 'm5', icon: '💡', text: 'Answer 10 questions today', reward: '+50 NeevCoins', target: 10, type: 'any_q', coins: 50,
    luckyAnnounce: (n,pet) => `${n}! Big mission today — 10 questions across ANY subject! 50 coins waiting for you! 💡`,
    luckyComplete: (n,pet) => `10 QUESTIONS DONE!! You're unstoppable today! 💡🔥` },
  { id: 'm6', icon: '➗', text: 'Beat your best score in Maths', reward: 'Champion Coin + 30 coins', target: 1, type: 'maths_best', coins: 30,
    luckyAnnounce: (n,pet) => `${n}, today's challenge: beat your own Maths record! Your best is waiting to be broken! ➗`,
    luckyComplete: (n,pet) => `NEW RECORD!! Maths champion energy! I'm SO proud! ➗🏆` },
  { id: 'm7', icon: '🏆', text: 'Earn a certificate today', reward: 'Legend Chest + 100 coins', target: 1, type: 'certificate', coins: 100,
    luckyAnnounce: (n,pet) => `${n}! LEGENDARY mission today — earn a certificate! 100 coins if you do it! You've got this! 🏆`,
    luckyComplete: (n,pet) => `A CERTIFICATE!! I'm crying happy tears!! This goes in the hall of fame! 🏆👑` },
  { id: 'm8', icon: '📖', text: 'Complete a full CBSE chapter quiz', reward: '+40 NeevCoins', target: 1, type: 'cbse_chapter', coins: 40,
    luckyAnnounce: (n,pet) => `${n}, today's mission: conquer a full CBSE chapter! One chapter, all questions. Let's go! 📖`,
    luckyComplete: (n,pet) => `Full chapter DONE! Your teachers would be so proud right now! 📖⭐` },
  { id: 'm9', icon: '🎯', text: 'Finish the Daily Challenge', reward: '+25 NeevCoins', target: 1, type: 'daily_challenge', coins: 25,
    luckyAnnounce: (n,pet) => `${n}! Daily Challenge is live — finish it today and earn 25 coins! I'll be cheering! 🎯`,
    luckyComplete: (n,pet) => `Daily Challenge: CRUSHED! That's what I'm talking about! 🎯` },
  { id: 'm10', icon: '🤖', text: 'Ask the AI Tutor 3 questions', reward: '+20 NeevCoins', target: 3, type: 'ai_tutor', coins: 20,
    luckyAnnounce: (n,pet) => `${n}, today's mission: ask me 3 questions! Anything! I love when you're curious! 🤖`,
    luckyComplete: (n,pet) => `3 questions asked — your curiosity is my favourite thing about you! 🤖💛` },
];

// Export so PetCompanion can announce mission on login
export function getTodayMissionAnnouncement(childName, petType) {
  const mission = getTodayMission();
  if (getMissionClaimed(mission.id)) return null;
  return mission.luckyAnnounce?.(childName, petType) || null;
}

export function getTodayMissionCompletion(childName, petType) {
  const mission = getTodayMission();
  return mission.luckyComplete?.(childName, petType) || null;
}

// Pick a deterministic mission for today (same all day)
function getTodayMission() {
  const today = new Date().toDateString();
  const key = `ns_mission_${today}`;
  const stored = localStorage.getItem(key);
  if (stored) return JSON.parse(stored);
  // Pick based on day-of-year so it rotates
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const mission = MISSIONS[dayOfYear % MISSIONS.length];
  localStorage.setItem(key, JSON.stringify(mission));
  return mission;
}

function getMissionProgress(missionId) {
  const key = `ns_mission_prog_${todayIST()}_${missionId}`;
  return parseInt(localStorage.getItem(key) || '0', 10);
}

function getMissionClaimed(missionId) {
  const key = `ns_mission_claimed_${todayIST()}_${missionId}`;
  return !!localStorage.getItem(key);
}

function setMissionClaimedLocal(missionId) {
  localStorage.setItem(`ns_mission_claimed_${todayIST()}_${missionId}`, '1');
}

// Synchronous — localStorage update is instant, Supabase fires in background
// Callers do NOT need await
export function incrementMissionProgress(type, amount = 1) {
  const mission = getTodayMission();
  if (mission.type !== type) return;
  if (getMissionClaimed(mission.id)) return;

  const key = `ns_mission_prog_${todayIST()}_${mission.id}`;
  const current = getMissionProgress(mission.id);
  const next = Math.min(current + amount, mission.target);
  localStorage.setItem(key, String(next));

  // Sync to Supabase in background — fire and forget
  getEmailFromSession().then(email => {
    if (email) saveMissionToDB(email, mission.id, next, false).catch(() => {});
  }).catch(() => {});
}

export default function MysteryMission({ onClaim, profile, refreshTrigger = 0 }) {
  const [revealed, setRevealed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [claimed, setClaimed] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const mission = getTodayMission();

  // Load from Supabase on mount — overrides localStorage if newer
  useEffect(() => {
    if (!profile?.parent_email) return;
    loadMissionFromDB(profile.parent_email).then(row => {
      if (!row) return;
      if (row.progress > getMissionProgress(mission.id)) {
        localStorage.setItem(`ns_mission_prog_${todayIST()}_${mission.id}`, String(row.progress));
        setProgress(row.progress);
      }
      if (row.claimed) {
        setMissionClaimedLocal(mission.id);
        setClaimed(true);
      }
    }).catch(() => {});
  }, [profile?.parent_email]); // eslint-disable-line

  // Inject CSS
  useEffect(() => {
    if (!document.getElementById('mm-css')) {
      const s = document.createElement('style');
      s.id = 'mm-css';
      s.textContent = css;
      document.head.appendChild(s);
    }
  }, []);

  // Load state
  useEffect(() => {
    const revealKey = `ns_mission_revealed_${new Date().toDateString()}`;
    if (localStorage.getItem(revealKey)) setRevealed(true);
    setProgress(getMissionProgress(mission.id));
    setClaimed(getMissionClaimed(mission.id));
  }, [mission.id]);

  // Refresh progress on mount and when refreshTrigger changes (user returns to home)
  useEffect(() => {
    const p = getMissionProgress(mission.id);
    setProgress(p);
    if (p >= mission.target && !getMissionClaimed(mission.id)) {
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2000);
    }
  }, [mission.id, mission.target, refreshTrigger]);

  const handleReveal = () => {
    const revealKey = `ns_mission_revealed_${new Date().toDateString()}`;
    localStorage.setItem(revealKey, '1');
    setRevealed(true);
    playSound('sparkle');
  };

  const handleClaim = (e) => {
    e.stopPropagation();
    if (claimed || progress < mission.target) return;
    setMissionClaimedLocal(mission.id);
    setClaimed(true);
    playSound('levelup');
    onClaim && onClaim(mission.coins);
    // Save claimed to Supabase
    if (profile?.parent_email) {
      saveMissionToDB(profile.parent_email, mission.id, progress, true).catch(() => {});
    }
  };

  const pct = Math.min(100, Math.round((progress / mission.target) * 100));
  const complete = progress >= mission.target;

  return (
    <div className={`mm-card ${showCelebration ? 'mm-complete-pulse' : ''}`}
      onClick={!revealed ? handleReveal : undefined}
      style={{ margin: '0', padding: '14px 16px' }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: revealed ? 10 : 0 }}>
        <div style={{
          background: 'rgba(255,214,0,0.15)',
          border: '1.5px solid rgba(255,214,0,0.4)',
          borderRadius: 10, padding: '4px 10px',
          fontSize: 11, fontWeight: 900,
          color: '#FFD600', letterSpacing: '0.07em', whiteSpace: 'nowrap',
        }}>
          🎯 MYSTERY MISSION
        </div>
        <div style={{ flex: 1 }} />
        {!revealed && (
          <span className="mm-lock-icon">🔒</span>
        )}
        {revealed && !claimed && !complete && (
          <span style={{ fontSize: 12, color: 'rgba(255,214,0,0.6)', fontWeight: 700 }}>
            Today only
          </span>
        )}
        {revealed && !claimed && complete && (
          <span style={{
            background: '#FF2D55', color: 'white',
            borderRadius: 20, padding: '3px 10px',
            fontSize: 11, fontWeight: 900,
            animation: 'mm-badge-pulse 1.2s ease-in-out infinite',
          }}>🎁 CLAIM NOW</span>
        )}
        {claimed && (
          <span style={{ fontSize: 12, color: '#00C851', fontWeight: 900 }}>✅ Done!</span>
        )}
      </div>

      {/* Locked state */}
      {!revealed && (
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 700, margin: 0 }}>
            Tap to reveal today's secret mission →
          </p>
        </div>
      )}

      {/* Revealed state */}
      {revealed && (
        <div className="mm-revealed">
          {/* Mission text */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 28, lineHeight: 1 }}>{mission.icon}</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: 'white', lineHeight: 1.4 }}>
                {mission.text}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,214,0,0.8)', fontWeight: 700 }}>
                🎁 Reward: {mission.reward}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, height: 8, overflow: 'hidden', marginBottom: 6 }}>
            <div style={{
              height: '100%',
              width: `${pct}%`,
              background: complete ? 'linear-gradient(90deg,#00C851,#00E676)' : 'linear-gradient(90deg,#FFD600,#FF9500)',
              borderRadius: 8,
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700 }}>
              {progress}/{mission.target} {complete ? '— Complete! 🎉' : ''}
            </p>
            {complete && !claimed && (
              <button onClick={handleClaim} style={{
                background: 'linear-gradient(135deg,#FFD600,#FF9500)',
                border: 'none', borderRadius: 10, padding: '5px 14px',
                fontSize: 12, fontWeight: 900, color: '#1A1A2E',
                cursor: 'pointer', fontFamily: "'Nunito', sans-serif",
              }}>
                Claim 🪙
              </button>
            )}
            {!complete && (
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,214,0,0.6)', fontWeight: 800 }}>
                {pct}%
              </p>
            )}
          </div>

          {/* Celebration */}
          {showCelebration && (
            <div style={{
              marginTop: 8, background: 'rgba(0,200,81,0.15)',
              borderRadius: 10, padding: '6px 12px', textAlign: 'center',
              border: '1px solid rgba(0,200,81,0.3)',
            }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 900, color: '#00E676' }}>
                🎉 Mission Complete! Tap Claim →
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
